import { useEffect } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import {
  FileText, Package, MessageSquare, WifiOff, FileDigit,
  PenLine, ArrowRight, CheckCircle2, Camera, Mic, ChevronDown,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import LangueSwitcher from "@/components/LangueSwitcher";
import { useAuth } from "@/context/AuthContext";
import { LANGUES, appliquerLangue } from "@/i18n";

const MODULE_ICONS = { "devis-factures": FileText, "stock-materiel": Package, "communication-client": MessageSquare, "comptes-rendus": Mic };
const ATOUT_ICONS = [WifiOff, Camera, PenLine, FileDigit];

export default function Landing() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // Meta SEO multilingues : title, description et attribut lang suivent la langue active.
  useEffect(() => {
    document.title = t("landing.meta_title");
    document.documentElement.lang = i18n.language.slice(0, 2);
    const description = t("landing.meta_description");
    const title = t("landing.meta_title");
    const updates = [
      ['meta[name="description"]', description],
      ['meta[property="og:title"]', title],
      ['meta[property="og:description"]', description],
    ];
    for (const [selector, value] of updates) {
      document.querySelector(selector)?.setAttribute("content", value);
    }
  }, [i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  // Priorité : 1) paramètre ?lang= (liens hreflang/partage), 2) choix manuel mémorisé,
  // 3) langue du navigateur (jamais pour les robots : le français reste la version indexée).
  useEffect(() => {
    const param = (searchParams.get("lang") || "").slice(0, 2).toLowerCase();
    if (LANGUES.some((l) => l.code === param)) {
      appliquerLangue(param);
      return;
    }
    if (localStorage.getItem("mastoras_langue")) return;
    if (/bot|crawl|spider|slurp|facebookexternalhit|lighthouse|headless/i.test(navigator.userAgent)) return;
    const nav = (navigator.language || "").slice(0, 2).toLowerCase();
    if (nav !== "fr" && LANGUES.some((l) => l.code === nav)) appliquerLangue(nav);
  }, [searchParams]);

  function copierPromo() {
    navigator.clipboard?.writeText("BIENVENUE10");
    toast.success(t("landing.promo_copie"));
  }

  const modules = t("landing.modules", { returnObjects: true });
  const atouts = t("landing.atouts", { returnObjects: true });
  const faqs = t("landing.faqs", { returnObjects: true });
  const heroPoints = t("landing.hero_points", { returnObjects: true });
  const tarifPoints = t("landing.tarif_points", { returnObjects: true });

  if (user) return <Navigate to="/tableau-de-bord" replace />;

  return (
    <div className="min-h-screen bg-[#12100D]" data-testid="page-accueil">
      <header className="max-w-6xl mx-auto flex items-center justify-between gap-2 px-4 sm:px-5 py-5">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Logo size={40} iconSize={22} />
          <span className="font-serif font-bold text-lg sm:text-xl text-white whitespace-nowrap">Mastoras</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <LangueSwitcher sombre />
          <Link to="/login" data-testid="btn-se-connecter"
                className="h-10 sm:h-11 px-2.5 sm:px-5 rounded-lg border border-[#D9A461]/40 text-[#D9A461] font-semibold text-xs sm:text-sm flex items-center whitespace-nowrap hover:bg-[#221F1A] transition-colors">
            {t("landing.se_connecter")}
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 pt-10 sm:pt-16 pb-14 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="text-[#D9A461] font-semibold text-sm tracking-wide uppercase mb-4">
            {t("landing.hero_badge")}
          </p>
          <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
            {t("landing.hero_titre_1")}{" "}
            <span className="text-[#D9A461]">{t("landing.hero_titre_2")}</span>
          </h1>
          <p className="text-stone-300 text-base sm:text-lg mt-5 max-w-lg">
            {t("landing.hero_texte")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to="/login?mode=inscription" className="btn-accent !h-14 !text-lg" data-testid="btn-creer-compte">
              {t("landing.hero_cta")} <ArrowRight size={20} />
            </Link>
            <a href="#modules" className="h-14 px-6 rounded-lg border border-white/15 text-stone-200 font-semibold flex items-center justify-center hover:bg-[#221F1A] transition-colors"
               data-testid="btn-decouvrir">
              {t("landing.hero_cta2")}
            </a>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 mt-8 text-sm text-stone-400">
            {heroPoints.map((p) => (
              <span key={p} className="flex items-center gap-1.5">
                <CheckCircle2 size={15} className="text-[#7BC496]" /> {p}
              </span>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#1A1713] p-2 shadow-2xl">
          <div className="flex gap-1.5 px-3 py-2.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#3A352E]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#3A352E]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#3A352E]" />
          </div>
          <picture>
            <source srcSet="/images/mastoras-demo-complet.webp" type="image/webp" />
            <img src="/images/mastoras-demo-complet.gif" alt={t("landing.apercu_alt")}
                 className="rounded-lg w-full border border-white/5" loading="lazy" data-testid="apercu-dashboard" />
          </picture>
        </div>
      </section>

      <section id="modules" className="bg-[#F7F4EE] rounded-t-[2rem] px-5 py-16">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif font-bold text-3xl sm:text-4xl text-[#221F1A]">
            {t("landing.modules_titre")}
          </h2>
          <p className="text-stone-600 text-base mt-3 max-w-2xl">
            {t("landing.modules_sous_titre")}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {modules.map((m) => {
              const Icon = MODULE_ICONS[m.id] || FileText;
              return (
                <div key={m.id} className="bg-white rounded-xl border border-[#E6DFD1] p-6"
                     data-testid={`module-${m.id}`}>
                  <div className="w-12 h-12 rounded-lg bg-[#12100D] flex items-center justify-center mb-4">
                    <Icon size={22} className="text-[#D9A461]" />
                  </div>
                  <h3 className="font-serif font-semibold text-xl text-[#221F1A]">{m.titre}</h3>
                  <p className="text-stone-600 text-sm mt-2">{m.texte}</p>
                  <ul className="mt-4 space-y-2">
                    {m.points.map((p) => (
                      <li key={p} className="text-sm text-stone-700 flex items-start gap-2">
                        <CheckCircle2 size={15} className="text-[#276440] mt-0.5 shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#F7F4EE] px-5 pb-16">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {atouts.map((label, i) => {
            const Icon = ATOUT_ICONS[i] || CheckCircle2;
            return (
              <div key={label} className="flex items-center gap-3 bg-[#EFEAE0] rounded-lg px-4 py-3.5">
                <Icon size={18} className="text-[#8A5A2B] shrink-0" />
                <span className="text-sm font-medium text-[#221F1A]">{label}</span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-[#F7F4EE] px-5 pb-20" data-testid="section-tarif">
        <div className="max-w-6xl mx-auto">
          <div className="bg-[#12100D] rounded-2xl border border-[#D9A461]/30 p-8 sm:p-10 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-serif font-bold text-3xl text-white">{t("landing.tarif_titre")}</h2>
              <p className="text-stone-400 text-sm mt-3">{t("landing.tarif_texte")}</p>
              <ul className="mt-5 space-y-2">
                {tarifPoints.map((p) => (
                  <li key={p} className="text-sm text-stone-300 flex items-start gap-2">
                    <CheckCircle2 size={15} className="text-[#7BC496] mt-0.5 shrink-0" /> {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center bg-[#1A1713] rounded-xl p-8 border border-white/10">
              <div className="font-serif font-bold text-5xl text-white">
                25 € <span className="text-lg font-normal text-stone-400">{t("landing.tarif_ht")}</span>
              </div>
              <p className="text-stone-400 text-sm mt-2">{t("landing.tarif_tva")}</p>
              <p className="text-[#7BC496] text-sm font-semibold mt-4 flex items-center justify-center gap-1.5">
                <CheckCircle2 size={15} /> {t("landing.tarif_essai")}
              </p>
              <div className="mt-5 rounded-lg border border-[#D9A461]/40 bg-[#D9A461]/10 px-4 py-3"
                   data-testid="promo-bienvenue20">
                <div className="text-[#D9A461] text-xs font-bold uppercase tracking-wide">{t("landing.promo_badge")}</div>
                <p className="text-sm text-stone-300 mt-1">
                  {t("landing.promo_texte")}{" "}
                  <button type="button" onClick={copierPromo}
                          title={t("landing.promo_copie")}
                          className="font-mono font-bold text-white underline decoration-dotted underline-offset-4 hover:text-[#D9A461] transition-colors"
                          data-testid="btn-copier-promo">
                    BIENVENUE10
                  </button>
                </p>
              </div>
              <Link to="/login?mode=inscription" className="btn-accent w-full mt-6 !h-12" data-testid="btn-creer-compte-tarif">
                {t("landing.tarif_cta")} <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white px-5 py-16" data-testid="section-faq">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif font-bold text-3xl sm:text-4xl text-center">{t("landing.faq_titre")}</h2>
          <p className="text-stone-500 mt-3 mb-8 text-center">{t("landing.faq_sous_titre")}</p>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={f.q}
                       className="group bg-white border border-[#E6DFD1] rounded-xl px-5 py-4 open:border-[#D9A461]/50 transition-colors"
                       data-testid={`faq-item-${i}`}>
                <summary className="font-semibold text-[15px] cursor-pointer list-none flex items-center justify-between gap-3">
                  {f.q}
                  <ChevronDown size={18} className="text-[#8A5A2B] shrink-0 transition-transform group-open:rotate-180" />
                </summary>
                <p className="text-sm text-stone-600 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#12100D] px-5 py-16 text-center">
        <h2 className="font-serif font-bold text-3xl sm:text-4xl text-white">
          {t("landing.cta_titre")}
        </h2>
        <p className="text-stone-400 mt-3 max-w-xl mx-auto">
          {t("landing.cta_texte")}
        </p>
        <Link to="/login?mode=inscription" className="btn-accent !h-14 !text-lg inline-flex mt-8"
              data-testid="btn-creer-compte-bas">
          {t("landing.cta_bouton")} <ArrowRight size={20} />
        </Link>
      </section>

      <footer className="bg-[#0D0B09] px-5 py-8">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Logo size={32} iconSize={17} />
            <span className="font-serif font-semibold text-stone-300">Mastoras</span>
          </div>
          <p className="text-stone-500 text-xs text-center">{t("landing.footer")}</p>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/cgv" className="text-stone-500 hover:text-stone-300" data-testid="lien-cgv">{t("landing.cgv")}</Link>
            <Link to="/confidentialite" className="text-stone-500 hover:text-stone-300" data-testid="lien-confidentialite">
              {t("landing.confidentialite")}
            </Link>
            <button type="button" onClick={() => window.tarteaucitron?.userInterface?.openPanel?.()}
                    className="text-stone-500 hover:text-stone-300" data-testid="lien-cookies">
              {t("landing.cookies")}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
